import DraftService from "@/services/draft.service";
import SharedFunctions from "@/shared/SharedFunctions";
import DraftFoundDialog from "@/components/seller/dialogs/DraftFoundDialog";
import DraftSaveButton from "@/components/seller/DraftSaveButton";   // ← חדש
/*
  מיקסין לניהול טיוטות של הקמת מנוי.

  מה הוא מוסיף לקומפוננטה:
    data       - draftDialog, foundDraft, draftId, draftSaving, draftNeedsIdImage
    computed   - draftType, draftPlanName
    methods    - saveDraft, lookupDraft, applyDraft, dismissDraft, deleteDraft
    components - DraftFoundDialog (מיקסין ממזג גם components, אין צורך לרשום ידנית)

  הוא מסתמך על ה-state הקיים של CreateManuiFirstComp:
    customerDetails, payDetails, simDetails, costumeForwardDetails,
    operationCompanySelected, selectedPlan, clickedButtonIndex, customerExist,
    showFields, e1, plans, cities, streets, branches, idImage, adminSkipJ5, levels
  ועל המתודות showSnackBar ו-getLevels.
*/

// אותן רשימות בדיוק כמו בקונטרולר. פרטי האשראי (card_number/tokef/cvv) לא נמצאים
// כאן בכוונה - הם לא נשמרים בטיוטה, והמשווק מזין אותם מחדש.
const CUSTOMER_FIELDS = ["name", "family", "business_name", "card_id", "phone1",
  "phone2", "email", "address", "building", "city", "postal_code", "community_id"];
const PAY_FIELDS = ["payType", "bankCode", "branchCode", "accountCode"];
const SIM_FIELDS = ["manuiType", "abandoned_company", "improve_mobility", "simNumber",
  "manuiNumber", "community_level", "jingle", "autoConnectLine"];
const FORWARD_FIELDS = ["isChecked", "forwardName", "forwardFamily", "forwardCardId"];

const EMPTY_PAY = { payType: "", bankCode: "", branchCode: "", accountCode: "", card_number: "", tokef: "", cvv: "" };
const EMPTY_SIM = {
  manuiType: "", abandoned_company: "", improve_mobility: false, simNumber: "",
  manuiNumber: "", community_level: "", jingle: 0, autoConnectLine: true
};
const EMPTY_FORWARD = { isChecked: false, forwardName: "", forwardFamily: "", forwardCardId: "" };

const emptyFrom = (fields) => fields.reduce((acc, f) => { acc[f] = ""; return acc; }, {});

const pick = (obj, fields) => {
  const out = {};
  if (!obj || typeof obj !== "object") return out;
  for (const f of fields) {
    if (obj[f] !== undefined && obj[f] !== null) out[f] = obj[f];
  }
  return out;
};

// ה-combobox של עיר/רחוב מחזיר לפעמים אובייקט {code, name} ולפעמים מחרוזת
const toText = (v) => (v && typeof v === "object" ? (v.name || "") : (v || ""));

export default {
  components: { DraftFoundDialog, DraftSaveButton },

  data: () => ({
    draftDialog: false,
    foundDraft: null,
    draftId: null,          // נשלח לשרת ב-FormData בסיום, כדי שתדע איזו טיוטה למחוק
    draftSaving: false,
    draftNeedsIdImage: false,
    draftLastSavedAt: null,
  }),

  computed: {
    // אחרי אישור הת"ז אנחנו יודעים אם הלקוח קיים, ומזה נגזר הסוג.
    // "יצירת לקוח ללא מנוי" נבחר רק בשלב 4, ולכן דורס את הדיפולט בשמירה מאוחרת.
    draftType() {
      if (this.simDetails.manuiType === "יצירת לקוח ללא מנוי") return "customer_only";
      return this.customerExist ? "manui_only" : "customer_and_manui";
    },
    draftPlanName() {
      if (!this.foundDraft || !this.foundDraft.plan_id) return "";
      const plan = this.plans.find((p) => p._id === this.foundDraft.plan_id);
      return plan ? plan.name : "";
    },
  },

  methods: {
    buildDraftSnapshot() {
      const customer = pick(this.customerDetails, CUSTOMER_FIELDS);
      customer.city = toText(customer.city);
      customer.address = toText(customer.address);

      return {
        version: 1,
        customerExist: this.customerExist,
        adminSkipJ5: this.adminSkipJ5,
        operationCompanySelected: this.operationCompanySelected || "",
        customerDetails: customer,
        payDetails: pick(this.payDetails, PAY_FIELDS),
        simDetails: pick(this.simDetails, SIM_FIELDS),
        costumeForwardDetails: pick(this.costumeForwardDetails, FORWARD_FIELDS),
      };
    },

    /**
     * @param {'auto'|'manual'} savedBy  auto = אחרי שגיאת שרת, manual = לחיצה על "שמור טיוטה"
     * @param {string} failReason        הודעת השגיאה שהתקבלה, לתצוגה בדיאלוג
     * @returns {Promise<boolean>}
     */
    async saveDraft(savedBy = "auto", failReason = "") {
      if (!this.customerDetails.card_id || !this.showFields) {
        if (savedBy === "manual") {
          this.showSnackBar("יש להזין תעודת זהות וללחוץ על אישור לפני שמירת טיוטה", "red");
        }
        return false;
      }

      try {
        this.draftSaving = true;

        const payload = {
          card_id: String(this.customerDetails.card_id).trim(),
          type: this.draftType,
          manui_type: this.simDetails.manuiType || "",
          manui_number: this.simDetails.manuiNumber || "",
          sim_number: this.simDetails.simNumber || "",
          operating_company: this.operationCompanySelected || "",
          plan_id: (this.selectedPlan && this.selectedPlan._id) || null,
          customer_name: `${this.customerDetails.name || ""} ${this.customerDetails.family || ""}`.trim(),
          last_step: this.e1,
          saved_by: savedBy,
          fail_reason: failReason,
          needs_id_image: !!this.idImage,
          snapshot: this.buildDraftSnapshot(),
        };

        const saved = await DraftService.upsertDraft(payload);
        this.draftSaving = false;

        if (!saved || !saved._id) {
          if (savedBy === "manual") this.showSnackBar("שמירת הטיוטה נכשלה, נסו שוב", "red");
          return false;
        }

        this.draftId = saved._id;
        this.draftLastSavedAt = new Date().toISOString();     // ← חדש
        if (savedBy === "manual") this.showSnackBar("הטיוטה נשמרה", "green");
        return true;
      } catch (error) {
        this.draftSaving = false;
        console.error(error);
        if (savedBy === "manual") this.showSnackBar("שמירת הטיוטה נכשלה, נסו שוב", "red");
        return false;
      }
    },

    // נקרא בסוף checkId, אחרי שנקבע customerExist
    async lookupDraft() {
      if (!this.customerDetails.card_id) return;

      const draft = await DraftService.getDraftByCardId(String(this.customerDetails.card_id).trim());
      if (!draft || !draft._id) return;

      this.foundDraft = draft;
      this.draftDialog = true;
    },

    async applyDraft(draft) {
      if (!draft || !draft.snapshot) return;

      try {
        this.draftDialog = false;
        this.progressShow = true;
        const s = draft.snapshot;

        // ── 1. פרטי הלקוח ──
        if (this.customerExist) {
          // הלקוח נטען כבר מהמסד ב-checkId, וזה מקור האמת. מחזירים מהטיוטה
          // רק את שלושת השדות שהמשווק יכול לשנות ללקוח קיים.
          const c = s.customerDetails || {};
          ["city", "address", "building"].forEach((k) => {
            if (c[k]) this.$set(this.customerDetails, k, c[k]);
          });
        } else {
          this.customerDetails = Object.assign(
            emptyFrom(CUSTOMER_FIELDS),
            pick(s.customerDetails, CUSTOMER_FIELDS)
          );
          if (!this.customerDetails.card_id) this.customerDetails.card_id = draft.card_id;

          // קהילה מהטיוטה שכבר לא מאושרת למשווק - לא משחזרים אותה, והמשווק יבחר מחדש
          if (this.customerDetails.community_id &&
            !this.communities.some((c) => c._id === this.customerDetails.community_id)) {
            this.customerDetails.community_id = "";
          }
        }

        // ── 2. רשימת הרחובות לעיר שהוזנה, אחרת ה-combobox יהיה ריק ──
        await this.loadDraftStreets(this.customerDetails.city);

        // ── 3. רמות הסינון לשלב 4. בשחזור דילגנו על validateAndNext שקורא לזה ──
        if (this.customerDetails.community_id) {
          await this.getLevels();
        }

        // ── 4. חבילה: קודם החברה המפעילה, אחר כך nextTick (ה-watcher מאפס את
        //        selectedPlan ו-clickedButtonIndex), ורק אז החבילה עצמה ──
        if (s.operationCompanySelected) {
          this.operationCompanySelected = s.operationCompanySelected;
          await this.$nextTick();

          if (draft.plan_id) {
            // מאתרים את החבילה מחדש מ-plans, כדי שמחיר ופרטים יהיו מעודכנים
            const plan = this.plans.find((p) => p._id === draft.plan_id);
            if (plan) {
              this.selectedPlan = plan;
              // clickedButtonIndex הוא אינדקס בתוך filteredPlans, לא בתוך plans
              this.clickedButtonIndex = this.filteredPlans.findIndex((p) => p._id === plan._id);
            }
          }
        }

        // ── 5. תשלום. לפני השמה של branchCode צריך שרשימת הסניפים תהיה טעונה ──
        const pay = Object.assign({}, EMPTY_PAY, pick(s.payDetails, PAY_FIELDS));
        if (!this.customerExist && pay.payType === "bankHok" && pay.bankCode) {
          this.branches = pay.bankCode === "9"
            ? [{ Branch_Code: "01", Branch_Address: "כללי", City: "" }]
            : await SharedFunctions.getBanksBranches(pay.bankCode);
        }
        this.payDetails = pay;

        // ── 6. פרטי סים ופרטי מקבל שונים ──
        this.simDetails = Object.assign({}, EMPTY_SIM, pick(s.simDetails, SIM_FIELDS));
        this.costumeForwardDetails = Object.assign({}, EMPTY_FORWARD, pick(s.costumeForwardDetails, FORWARD_FIELDS));
        if (typeof s.adminSkipJ5 === "boolean" && this.operateByAdmin) {
          this.adminSkipJ5 = s.adminSkipJ5;
        }

        // ── 7. autoConnectLine אחרון: ה-watcher עם immediate דורס אותו
        //        כשמשתנים manuiType או החברה המפעילה ──
        await this.$nextTick();
        if (s.simDetails && typeof s.simDetails.autoConnectLine === "boolean") {
          this.simDetails.autoConnectLine = s.simDetails.autoConnectLine;
        }

        this.draftId = draft._id;
        this.draftNeedsIdImage = !!draft.needs_id_image;
        this.foundDraft = null;
        this.e1 = 1; // נשארים בשלב הראשון כדי שהמשווק יעבור על הפרטים

        // מנקים ולידציות כדי שהמשווק לא יקבל שדות אדומים לפני שנגע במשהו
        await this.$nextTick();
        ["form1", "form2", "form3", "form4"].forEach((r) => {
          if (this.$refs[r]) this.$refs[r].resetValidation();
        });

        this.progressShow = false;

        this.showSnackBar(
          this.draftNeedsIdImage
            ? "הפרטים מולאו מהטיוטה. יש לצרף מחדש את צילום תעודת הזהות ולהזין את פרטי התשלום"
            : "הפרטים מולאו מהטיוטה. יש להזין מחדש את פרטי התשלום",
          "green"
        );
      } catch (error) {
        this.progressShow = false;
        console.error(error);
        this.showSnackBar("טעינת הטיוטה נכשלה: " + error, "red");
      }
    },

    async loadDraftStreets(city) {
      const cityName = toText(city).trim();
      if (!cityName || !this.cities.length) return;

      const cityObj = this.cities.find((c) => c["שם_ישוב"].trim() === cityName);
      if (cityObj) {
        this.streets = await SharedFunctions.getStreetsList(cityObj["סמל_ישוב"]);
      }
    },

    // סוגר את הדיאלוג ומשאיר את הטיוטה במסד לשימוש בפעם הבאה
    dismissDraft() {
      this.draftDialog = false;
      this.foundDraft = null;
    },

    async deleteDraft() {
      const draft = this.foundDraft;
      if (!draft || !draft._id) return this.dismissDraft();

      this.progressShow = true;
      const deleted = await DraftService.deleteDraft(draft._id);
      this.progressShow = false;

      if (!deleted) return this.showSnackBar("מחיקת הטיוטה נכשלה, נסו שוב", "red");

      if (this.draftId === draft._id) this.draftId = null;
      this.dismissDraft();
      this.showSnackBar("הטיוטה נמחקה", "green");
    },
  },
};